// Quote Builder — Door picker (port of kos-qb-v3.js:9368–9794
// `renderDoorConfigurator` + `_qbv3w6*` handlers).
//
// CORRECT picker (replaces the WRONG `renderDoorPicker` port that lived
// here previously):
//   • Colour <select> with optgroups (Stock / Signature Select / PTO /
//     Bespoke) and tier badge below
//   • Height (mm) → Width (mm) dropdowns + resolved size strip with price
//   • Drilling & Handing card with NONE / 35mm Hinge mode toggle
//   • Unit type: BASE | WALL  ·  Handing: LEFT | RIGHT | TOP
//   • Qty of holes (2/3/4/5) + Top distance + middle-hole distances
//     (qty−2 of them) + Bottom distance
//   • Zone + Qty row
//
// Retired (per Steve, 2026-05-15): MTM toggle, Integrated handle section,
// Lift-up handing — stripped from this picker but kept on the QuoteItem
// type for possible future re-use.
//
// Wires to (repointed 2026-05-24 onto the PG-backed supplier catalogue):
//   • GET /api/catalogue/door-ranges?supplier=tkc            — range list (cached)
//   • GET /api/catalogue/doors/sizes?supplier=tkc&range=&colour=  — sizes+prices
//   • GET /api/catalogue/door-range-extras?supplier=tkc&range=  — panels & accessories
//                                                                  (added 2026-05-25)
//
// `type` ("stock"/"pto"/"bespoke") is still passed in the URL for legacy
// compat; the new server-side endpoint accepts and ignores it. The picker UI
// continues to partition colours into tier optgroups client-side.
//
// The picker is mounted from picker-area.ts when the user has the Doors
// chip selected. Mount API kept stable: `mountDoorPicker(host, store)`.

import { api } from "@tkos/api-client";
import { repaintPreservingScroll } from "@tkos/shared-ui";
import type { DoorDefaults, QuoteItem, TkcRange } from "./types";
import { loadDiscountsFor, resolveDiscPct } from "./discounts";
import { loadDrillingChargeFor, resolveDrillingCharge } from "./drilling-charge";
import { aggregateDrilling } from "./drilling-aggregate";
import type { QuoteStore } from "./state";
import { escapeHtml, fmtGbp, ICONS, newId, svgIcon } from "./formatters";
import { recalcItem } from "./pricing";
import { bumpFav, fuzzyFilter } from "./fuzzy";
import { consumePickerSeed, getDoorSupplier } from "./picker-selection";
import {
  renderColourSelect,
  renderTierBadge,
  type DoorType,
} from "./door-colour-select";
import {
  renderExtrasSection,
  wireExtrasSection,
  fetchExtras,
  inferExtraCategory,
  type ExtraItem,
} from "./door-extras-section";
import {
  renderDrillingBlock,
  wireDrillingBlock,
  type DrillingSpec,
} from "./door-drilling-block";
import {
  setBreadcrumb,
  setBreadcrumbClearHandler,
  clearBreadcrumb,
} from "./picker-breadcrumb";

// ── Range cache (module-level; matches CAT.tkcRanges from source 581) ───

interface RangeCache {
  ranges: TkcRange[] | null;
  loading: boolean;
  callbacks: Array<(r: TkcRange[]) => void>;
}
// Keyed by supplier slug ("tkc", "pws", …) so multiple catalogue suppliers
// can each cache their own door ranges without colliding.
const RANGE_CACHES = new Map<string, RangeCache>();
function rangeCacheFor(slug: string): RangeCache {
  let c = RANGE_CACHES.get(slug);
  if (!c) { c = { ranges: null, loading: false, callbacks: [] }; RANGE_CACHES.set(slug, c); }
  return c;
}

// Session-level bespoke colour reference (Steve, 2026-05-26).
// One value applies to EVERY bespoke door added during the page session,
// so the retailer enters it once and any subsequent bespoke add picks it
// up automatically. Editable from the top of the configurator. Survives
// picker re-mounts because it lives on the module, not on per-mount state.
let sessionBespokeRef = "";

export function _setSessionBespokeRefForTests(v: string): void {
  sessionBespokeRef = v;
}

async function loadRanges(slug: string): Promise<TkcRange[]> {
  const cache = rangeCacheFor(slug);
  if (cache.ranges) return cache.ranges;
  if (cache.loading) {
    return new Promise<TkcRange[]>((resolve) => {
      cache.callbacks.push(resolve);
    });
  }
  cache.loading = true;
  try {
    const data = await api<{ total: number; ranges: TkcRange[] }>(
      `/catalogue/door-ranges?supplier=${encodeURIComponent(slug)}`,
    );
    cache.ranges = data.ranges ?? [];
  } catch {
    cache.ranges = [];
  }
  cache.loading = false;
  for (const cb of cache.callbacks) cb(cache.ranges);
  cache.callbacks = [];
  return cache.ranges;
}

// Colour tier sections (Signature / PTO / Bespoke) are no longer hardcoded
// in the UI — the dropdown is driven entirely by colours that exist in the
// supplier's uploaded catalogue (see render() below, 2026-05-25). If/when
// catalogue rows gain a tier marker, this picker can re-add the optgroups
// off that data rather than off the legacy hardcoded lists.

// Panel/plinth sizes that belong in the Panels category — source 9438.
const PANEL_EXCL: ReadonlyArray<readonly [number, number]> = [
  [900, 650], [2400, 650], [2400, 900], [2100, 900], [1200, 900],
  [792, 350], [830, 350], [990, 375], [150, 3050],
];

// DoorType is defined in door-colour-select.ts, imported below.
type FetchType = "stock" | "pto" | "paintplus" | "bespoke" | "primed";
type DrillMode = "none" | "hinge";
type Side = "L" | "R" | "T";
type UnitType = "base" | "wall";

// ── Size loader — PG-backed /catalogue/doors/sizes (supplier=tkc) ──────

/** Door variant types surfaced in the catalogue (2026-05-25). Curved rows
 *  carry `w=0`; their height is parsed server-side from the product name. */
type SizeType = "standard" | "curved" | "glazed" | "frame";

interface CatalogueSize {
  sku: string;
  name?: string;
  h: number;
  w: number;
  price: number;
  type: SizeType;
  /** PG catalogue category name (e.g. "CAMBRIDGE FIR GREEN DOORS"). Used for
   *  the supplier-retailer discount lookup. */
  category?: string;
  /** The supplier's discount code (PWS "BMV"), served since 2026-10-01. */
  discountCode?: string;
  /** Stock type carried through from specification.stockType (added
   *  2026-05-26) — drives the picker's lead-time badge and the QuoteItem's
   *  doorType so the line ships with the correct tier label. */
  stockType?: DoorType;
  label?: string;
}

function typeSuffix(t: SizeType): string {
  if (t === "curved") return "Curved";
  if (t === "glazed") return "Glazed";
  if (t === "frame")  return "Frame";
  return "";
}

// Recover the descriptive remainder of a catalogue product name — the handing
// ("Left"/"Right"), grain direction ("Vertical Grain"/"Horizontal Grain") or
// other variant text the supplier CSV carries inside the description column.
// The numeric width label ("273mm") drops it, which makes two rows that share
// the same (h, w, type) — e.g. a Left- and a Right-handed corner door, or the
// vertical- and horizontal-grain Plana doors — indistinguishable in the width
// dropdown. We strip the leading "<h> X <w>" (or curved "<n>mm") dimension
// token, the Curved/Glazed/Frame variant word already shown as a type suffix,
// and a lone trailing "Door"/"Doors" that carries no information. Whatever is
// left is genuine descriptive text and is preserved verbatim.
function dimsDescriptor(name: string | undefined, type: SizeType): string {
  if (!name) return "";
  let d = String(name)
    .replace(/^\s*\d+\s*[xX]\s*\d+\s*/, "")  // "895 X 273 Left …" → "Left …"
    .replace(/^\s*\d+\s*mm\s*/i, "");        // curved "715mm Curved …" → "Curved …"
  if (type !== "standard") {
    const word = type === "curved" ? "curved" : type === "glazed" ? "glazed" : "frame";
    d = d.replace(new RegExp(`\\b${word}\\b`, "ig"), " ");
  }
  d = d.replace(/\s+/g, " ").trim();
  if (/^doors?$/i.test(d)) d = "";           // bare "Door"/"Doors" → nothing
  return d;
}

async function fetchSizes(slug: string, range: string, type: FetchType, colour: string): Promise<CatalogueSize[]> {
  if (!range) return [];
  const qs = new URLSearchParams({ supplier: slug, range, type });
  if (colour) qs.set("colour", colour);
  try {
    const data = await api<{ sizes: CatalogueSize[] }>(`/catalogue/doors/sizes?${qs.toString()}`);
    const sizes = Array.isArray(data.sizes) ? data.sizes : [];
    // Surface the handing / grain / variant text held in the product name as
    // `label` so the width dropdown, resolved strip and saved quote line can
    // all distinguish otherwise-identical (h, w, type) rows. A server-supplied
    // label (none today) always wins.
    return sizes.map((p) => ({
      ...p,
      label: p.label || dimsDescriptor(p.name, p.type),
    }));
  } catch {
    return [];
  }
}

interface DoorPickerState {
  zoneId: string;
  doorRange: TkcRange | null;
  rangeInput: string;
  rangeComboOpen: boolean;
  rangeMatches: TkcRange[];
  doorType: DoorType;
  doorColour: string;
  doorBespokeText: string;
  doorSize: { h: number; w: number; sku: string; price: number; label: string; type: SizeType; category: string; discountCode?: string } | null;
  sizes: CatalogueSize[] | null;
  loadingSizes: boolean;
  // Drilling
  doorDrillMode: DrillMode;
  doorUnitType: UnitType;
  doorSide: Side;
  doorHoleQty: number;
  doorHoleMidMm: number[];
  doorTopMm: number;
  doorBottomMm: number;
  doorQty: number;
  // Panels & Accessories — fetched once per range, persist qty input
  // across paint() calls keyed by extra.id.
  extras: ExtraItem[] | null;
  loadingExtras: boolean;
  extrasOpen: boolean;
  extrasQty: Record<string, number>;
}

function blankState(defaults: DoorDefaults, firstZoneId: string): DoorPickerState {
  return {
    zoneId: firstZoneId,
    doorRange: null,
    rangeInput: defaults.range ?? "",
    rangeComboOpen: false,
    rangeMatches: [],
    doorType: (defaults.doorType ?? "stock"),
    doorColour: defaults.finish ?? "",
    doorBespokeText: "",
    doorSize: null,
    sizes: null,
    loadingSizes: false,
    doorDrillMode: defaults.drillMode ?? "none",
    doorUnitType: defaults.doorUnitType ?? "base",
    doorSide: (defaults.drilling.side as Side) ?? "L",
    doorHoleQty: 2,
    doorHoleMidMm: [],
    doorTopMm: defaults.drilling.topMm,
    doorBottomMm: defaults.drilling.bottomMm,
    doorQty: 1,
    extras: null,
    loadingExtras: false,
    extrasOpen: false,
    extrasQty: {},
  };
}

// ── Public mount API ─────────────────────────────────────────────────────

export interface DoorPickerHandle {
  unmount(): void;
}

export function mountDoorPicker(host: HTMLElement, store: QuoteStore): DoorPickerHandle {
  const initial = store.get();
  const firstZoneId = initial.zones[0]?.id ?? "";
  const state = blankState(initial.defaults.door, firstZoneId);

  // Which supplier's doors this mount configures. Captured once at mount;
  // defaults to TKC so existing TKC flows are unchanged. Drives every
  // catalogue fetch (slug), discount/drilling lookup (slug), the breadcrumb
  // and the quote-line supplier (label).
  const supCtx = getDoorSupplier();

  // One-shot seed from left-pane click (range card or product card).
  const seed = consumePickerSeed();
  if (seed) {
    if (typeof seed["range"] === "string") state.rangeInput = seed["range"];
    if (typeof seed["finish"] === "string") state.doorColour = seed["finish"];
    // 2026-05-26: per-tier range cards seed stockType so the picker mounts
    // in the right mode without waiting for the user to pick a colour first.
    const seededTier = seed["stockType"];
    if (
      seededTier === "stock"
      || seededTier === "pto"
      || seededTier === "signature"
      || seededTier === "bespoke"
      || seededTier === "primed"
    ) {
      state.doorType = seededTier;
    }
  }

  // Warm the per-(supplier, retailer) discount cache. The picker pulls from
  // the same module-level cache as products-panel — no UI block on the
  // fetch since worst-case is one paint with discount=0 before the cache
  // settles, and we re-paint after any add anyway.
  void loadDiscountsFor(supCtx.slug);
  // Warm the per-supplier drilling-charge cache so the addDoorToQuote path
  // can append a Drilling line synchronously when hinge mode is selected.
  void loadDrillingChargeFor(supCtx.slug);

  // Kick off range cache, then resolve `rangeInput` to a TkcRange object if
  // possible (matches source's auto-bind to range from defaults).
  void loadRanges(supCtx.slug).then(() => {
    if (state.rangeInput && !state.doorRange) {
      const match = (rangeCacheFor(supCtx.slug).ranges ?? []).find(
        (r) => r.name.toLowerCase() === state.rangeInput.toLowerCase(),
      );
      if (match) {
        state.doorRange = match;
        // Classify a seeded colour against the range's coloursByType ONLY
        // when the seed didn't already pin a stockType (per-tier range cards
        // do). Otherwise a stock seed colour that happens to appear in the
        // supplier-level PTO palette would get wrongly re-routed to PTO.
        const tierSeeded = seed && (seed["stockType"] === "stock"
          || seed["stockType"] === "pto"
          || seed["stockType"] === "signature"
          || seed["stockType"] === "bespoke"
          || seed["stockType"] === "primed");
        if (!tierSeeded && state.doorColour && match.coloursByType) {
          const c = state.doorColour.toLowerCase();
          const inGroup = (arr: readonly string[]) => arr.some((x) => x.toLowerCase() === c);
          if      (inGroup(match.coloursByType.pto))       state.doorType = "pto";
          else if (inGroup(match.coloursByType.signature)) state.doorType = "signature";
          else if (inGroup(match.coloursByType.bespoke))   state.doorType = "bespoke";
          else                                              state.doorType = "stock";
        }
        // Preload sizes for the dominant tier.
        const preType: FetchType = state.doorType === "signature" ? "stock" : (state.doorType as FetchType);
        void refreshSizes(state.doorRange.name, preType, state.doorColour).then(paint);
        // Preload panels & accessories scoped to the seeded colour (if any).
        void refreshExtras(state.doorRange.name, state.doorColour).then(paint);
      }
    }
    paint();
  });

  /** Full repaint, wrapped so the LEFT pane keeps its scroll position and the
   *  field just used keeps focus — see @tkos/shared-ui repaint-preserving-scroll.ts. */
  function paint(): void {
    repaintPreservingScroll(host, () => {
      host.innerHTML = render(state, initial.defaults.door, store);
      wire();
      pushCrumbs();
    });
  }

  function pushCrumbs(): void {
    const crumbs = [
      { sup: supCtx.label, label: "" },
      { label: "Doors" },
    ];
    if (state.doorRange) crumbs.push({ label: state.doorRange.name });
    setBreadcrumb({ crumbs, canClear: !!state.doorRange });
    setBreadcrumbClearHandler(() => {
      const fresh = blankState(store.get().defaults.door, firstZoneId);
      Object.assign(state, fresh);
      paint();
    });
  }

  function wire(): void {
    const $ = <T extends HTMLElement>(sel: string): T | null => host.querySelector<T>(sel);
  
    // Zone select
    $<HTMLSelectElement>("[data-role='zone']")?.addEventListener("change", (e) => {
      state.zoneId = (e.target as HTMLSelectElement).value;
    });
    // Qty
    $<HTMLInputElement>("[data-role='qty']")?.addEventListener("input", (e) => {
      state.doorQty = Number((e.target as HTMLInputElement).value) || 1;
    });

    // Range fuzzy combobox
    const rangeInput = $<HTMLInputElement>("[data-role='range-input']");
    rangeInput?.addEventListener("input", (e) => {
      const v = (e.target as HTMLInputElement).value;
      state.rangeInput = v;
      state.rangeComboOpen = true;
      state.rangeMatches = filterRanges(v);
      paintCombo();
    });
    rangeInput?.addEventListener("focus", () => {
      state.rangeComboOpen = true;
      state.rangeMatches = filterRanges(state.rangeInput);
      paintCombo();
    });
    rangeInput?.addEventListener("blur", () => {
      window.setTimeout(() => { state.rangeComboOpen = false; paintCombo(); }, 200);
    });
    $("[data-role='range-combo']")?.addEventListener("mousedown", (e) => {
      const target = e.target as HTMLElement | null;
      const item = target?.closest<HTMLElement>("[data-range-idx]");
      if (!item) return;
      e.preventDefault();
      const idx = Number(item.dataset["rangeIdx"]);
      const picked = state.rangeMatches[idx];
      if (!picked) return;
      selectRange(picked);
    });

    // Remember toggles
    $<HTMLInputElement>("[data-role='remember-range']")?.addEventListener("change", (e) => {
      const on = (e.target as HTMLInputElement).checked;
      store.set((d) => { d.defaults.remember.range = on; });
    });
    $<HTMLInputElement>("[data-role='remember-finish']")?.addEventListener("change", (e) => {
      const on = (e.target as HTMLInputElement).checked;
      store.set((d) => { d.defaults.remember.finish = on; });
    });
    $<HTMLInputElement>("[data-role='remember-drilling']")?.addEventListener("change", (e) => {
      const on = (e.target as HTMLInputElement).checked;
      store.set((d) => { d.defaults.remember.drilling = on; });
    });

    // Colour unified select (Stock / Signature flow — legacy ordering).
    $<HTMLSelectElement>("[data-role='colour-select']")?.addEventListener("change", (e) => {
      setColourUnified((e.target as HTMLSelectElement).value);
    });
    // PTO post-size colour pick. doorType is already "pto" via the seed,
    // so we just set doorColour and repaint to refresh the tier badge + Add.
    $<HTMLSelectElement>("[data-role='pto-colour-select']")?.addEventListener("change", (e) => {
      state.doorColour = (e.target as HTMLSelectElement).value;
      paint();
    });
    // Legacy bespoke colour input — only present when the user reaches
    // bespoke via the colour optgroup (not via the new bespoke range
    // card). Updates the per-mount field that legacy code paths still
    // read. The new flow uses the top-of-configurator field below.
    $<HTMLInputElement>("[data-role='bespoke-input']")?.addEventListener("input", (e) => {
      state.doorBespokeText = (e.target as HTMLInputElement).value;
    });

    // Session-level bespoke colour reference — applies to EVERY bespoke
    // door added until the retailer changes it. Top of the configurator
    // when isBespokeFlow. Persists across picker re-mounts via the
    // module-level `sessionBespokeRef`. No full repaint per keystroke so
    // the caret stays put; only the Add-button disabled state is updated
    // inline.
    $<HTMLInputElement>("[data-role='session-bespoke-ref']")?.addEventListener("input", (e) => {
      sessionBespokeRef = (e.target as HTMLInputElement).value;
      const addBtn = host.querySelector<HTMLButtonElement>("[data-role='add-door']");
      if (addBtn) {
        const ok = !!sessionBespokeRef.trim() && !!state.doorSize;
        addBtn.disabled = !ok;
      }
    });

    // Height + Width selects
    $<HTMLSelectElement>("[data-role='height-select']")?.addEventListener("change", (e) => {
      setHeight(Number((e.target as HTMLSelectElement).value) || 0);
    });
    $<HTMLSelectElement>("[data-role='width-select']")?.addEventListener("change", (e) => {
      setSizeBySku((e.target as HTMLSelectElement).value);
    });

    // Drilling & handing — bound by the shared block. `persist` keeps this
    // picker's existing "Remember" behaviour; the block itself is agnostic.
    const drillBlock = $<HTMLElement>("[data-role='drill-block']");
    if (drillBlock) {
      const spec: DrillingSpec = {
        mode: state.doorDrillMode,
        unitType: state.doorUnitType,
        side: state.doorSide,
        holeQty: state.doorHoleQty,
        topMm: state.doorTopMm,
        bottomMm: state.doorBottomMm,
        midMm: state.doorHoleMidMm ?? [],
      };
      wireDrillingBlock(drillBlock, spec, {
        onChange: () => {
          state.doorDrillMode = spec.mode;
          state.doorUnitType = spec.unitType;
          state.doorSide = spec.side;
          state.doorHoleQty = spec.holeQty;
          state.doorHoleMidMm = spec.midMm;
          paint();
        },
        persist: (patch) => {
          store.set((d) => {
            if (patch.mode !== undefined) d.defaults.door.drillMode = patch.mode;
            if (patch.unitType !== undefined) d.defaults.door.doorUnitType = patch.unitType;
            if (patch.side !== undefined && d.defaults.remember.drilling) {
              d.defaults.door.drilling.side = patch.side;
            }
            if (patch.topMm !== undefined && d.defaults.remember.drilling) {
              d.defaults.door.drilling.topMm = patch.topMm;
            }
            if (patch.bottomMm !== undefined && d.defaults.remember.drilling) {
              d.defaults.door.drilling.bottomMm = patch.bottomMm;
            }
          });
          // Distances are typed, not clicked — mirror them back without a
          // repaint so the caret is not lost mid-entry.
          state.doorTopMm = spec.topMm;
          state.doorBottomMm = spec.bottomMm;
        },
      });
    }

    // Cancel / Add
    $<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", () => {
      const fresh = blankState(store.get().defaults.door, firstZoneId);
      Object.assign(state, fresh);
      paint();
    });
    $<HTMLButtonElement>("[data-role='add-door']")?.addEventListener("click", () => {
      addDoorToQuote();
    });
    $<HTMLButtonElement>("[data-role='add-door-manual']")?.addEventListener("click", () => {
      addBespokeManual();
    });

    // ── Panels & Accessories section ──────────────────────────────────
    wireExtrasSection(host, {
      extras: state.extras ?? [], open: state.extrasOpen,
      loading: state.loadingExtras, qtyById: state.extrasQty,
    }, {
      onToggle: () => { state.extrasOpen = !state.extrasOpen; paint(); },
      onQty: (id, n) => { state.extrasQty[id] = n; },
      onAdd: (item) => addExtraToQuote(item),
    });
  }

  function paintCombo(): void {
    const combo = host.querySelector<HTMLElement>("[data-role='range-combo']");
    if (!combo) return;
    if (!state.rangeComboOpen) { combo.classList.remove("is-open"); return; }
    combo.classList.add("is-open");
    if (state.rangeMatches.length === 0) {
      combo.innerHTML = `<div class="qb-door-combo__empty">No range matches "${escapeHtml(state.rangeInput)}" — type freely or wait for the catalogue to load</div>`;
      return;
    }
    combo.innerHTML = state.rangeMatches.map((r, i) => `
      <div class="qb-door-combo__item" data-range-idx="${i}" role="option">
        <span class="qb-door-combo__name">${escapeHtml(r.name)}</span>
        <span class="qb-door-combo__meta">${r.catalogueRange ?? ""}${r.colours.length ? ` · ${r.colours.length} colour${r.colours.length === 1 ? "" : "s"}` : ""}</span>
      </div>
    `).join("");
  }

  function filterRanges(query: string): TkcRange[] {
    const ranges = rangeCacheFor(supCtx.slug).ranges ?? [];
    const hits = fuzzyFilter(
      query,
      ranges,
      (r) => `${r.name} ${r.colours.join(" ")} ${r.catalogueRange}`,
      { threshold: 25, limit: 25, favKey: (r) => r.name },
    );
    return hits.map((h) => h.item);
  }

  function selectRange(range: TkcRange): void {
    state.doorRange = range;
    state.rangeInput = range.name;
    state.rangeComboOpen = false;
    // Reset downstream state.
    state.doorType = range.colours.length ? state.doorType : "stock";
    state.doorColour = "";
    state.doorSize = null;
    state.sizes = null;
    state.extras = null;
    state.extrasQty = {};
    bumpFav(range.name);
    store.set((d) => {
      if (d.defaults.remember.range) d.defaults.door.range = range.name;
    });
    // Preload sizes for the dominant tier (source 9344: stock → pto → bespoke).
    const preType: FetchType = "stock";
    void refreshSizes(range.name, preType, "").then(() => {
      // After preload, paint to surface the catalogue range/colour pickers.
      paint();
    });
    // Preload panels & accessories for this range in parallel with sizes.
    // No colour is bound yet on range select; the colour-change handler
    // re-fetches with the selected colour once the user picks one.
    void refreshExtras(range.name, "").then(paint);
    paint();
  }

  function setColourUnified(val: string): void {
    if (!val) {
      state.doorType = "stock";
      state.doorColour = "";
      state.doorSize = null;
      state.sizes = null;
      state.doorBespokeText = "";
      // Drop the colour filter on extras so any-colour panels reappear.
      if (state.doorRange) void refreshExtras(state.doorRange.name, "").then(paint);
      paint();
      return;
    }
    const sep = val.indexOf("|");
    const tier = (sep > 0 ? val.slice(0, sep) : val) as DoorType;
    const colour = sep > 0 ? val.slice(sep + 1) : "";
    state.doorType = tier;
    state.doorColour = colour;
    state.doorSize = null;
    state.sizes = null;
    state.doorBespokeText = "";
    store.set((d) => {
      if (d.defaults.remember.finish) d.defaults.door.finish = colour;
      d.defaults.door.doorType = tier;
    });
    // Source 9614-9624: fetch sizes for chosen tier; on empty, fall back to
    // all sizes for that tier (no colour filter).
    if (!state.doorRange) { paint(); return; }
    const fetchType: FetchType = tier === "signature" ? "stock" : (tier as FetchType);
    state.loadingSizes = true;
    paint();
    // Re-fetch panels & accessories scoped to the chosen colour so e.g.
    // picking Cambridge Fir Green hides Cambridge Fjord panels.
    void refreshExtras(state.doorRange.name, colour).then(paint);
    void fetchSizes(supCtx.slug, state.doorRange.name, fetchType, colour).then((sizes) => {
      if (sizes.length) {
        state.sizes = sizes;
        state.loadingSizes = false;
        paint();
      } else if (state.doorRange) {
        void fetchSizes(supCtx.slug, state.doorRange.name, fetchType, "").then((fb) => {
          state.sizes = fb;
          state.loadingSizes = false;
          paint();
        });
      }
    });
  }

  async function refreshSizes(rangeName: string, type: FetchType, colour: string): Promise<void> {
    state.loadingSizes = true;
    const sizes = await fetchSizes(supCtx.slug, rangeName, type, colour);
    state.sizes = sizes;
    state.loadingSizes = false;
  }

  // Always uses the picker's current tier — extras must be scoped to the
  // active door type, never to a stale doorType from before the user
  // switched cards. Callers pass colour only for stock/signature flows;
  // PTO/bespoke/primed panels are tier-scoped (no per-colour categories).
  async function refreshExtras(rangeName: string, colour: string): Promise<void> {
    state.loadingExtras = true;
    const items = await fetchExtras(supCtx.slug, rangeName, state.doorType, colour);
    state.extras = items;
    state.loadingExtras = false;
  }

  function addExtraToQuote(extra: ExtraItem): void {
    const qty = Math.max(1, Number(state.extrasQty[extra.id]) || 1);
    const supplierLabel = supCtx.label;
    const range = state.doorRange;
    // PG-configured discount only — no supplier default fallback.
    const discPct = resolveDiscPct(supCtx.slug, extra.category ?? null, range?.name ?? null, {
      discountCode: extra.discountCode ?? null, doorColour: extra.doorColour || state.doorColour || null, code: extra.sku,
    }) ?? 0;

    const item: QuoteItem = {
      id: newId("i"),
      category: inferExtraCategory(extra.name),
      supplier: supplierLabel,
      code: extra.sku || `TKC-EXTRA-${extra.id.slice(0, 8)}`,
      desc: extra.name,
      qty,
      width: extra.w || 0,
      height: extra.h || 0,
      depth: extra.d || 0,
      notes: extra.category ? `From ${extra.category}` : "",
      listPrice: Number(extra.price) || 0,
      discPct,
      tradePrice: 0,
      markupMode: "pct",
      markupPct: store.get().defaults.markupPct ?? 0,
      markupGbp: 0,
      retailPrice: 0,
      doorRange: range?.name ?? extra.doorRange ?? "",
      doorColour: extra.doorColour ?? "",
    };
    recalcItem(item, null);

    const zoneId = state.zoneId || store.get().zones[0]?.id || "";
    store.set((d) => {
      const z = d.zones.find((zo) => zo.id === zoneId) ?? d.zones[0];
      if (z) z.items.push(item);
    });
    // Reset that line's qty input after a successful add so accidental
    // double-clicks don't add twice.
    state.extrasQty[extra.id] = 1;
    paint();
  }

  function setHeight(h: number): void {
    if (!h) { state.doorSize = null; paint(); return; }
    // Height pick clears any prior width/sku selection — the new height
    // may not have the same widths available. type defaults to standard
    // until the user picks a width.
    state.doorSize = { h, w: 0, sku: "", price: 0, label: "", type: "standard", category: "" };
    paint();
  }

  function setSizeBySku(sku: string): void {
    if (!sku || !state.doorSize) { paint(); return; }
    const valid = (state.sizes ?? []).filter((p) =>
      p.h > 0 &&
      (p.w > 0 || p.type === "curved") &&
      !PANEL_EXCL.some(([ph, pw]) => ph === p.h && pw === p.w),
    );
    const found = valid.find((p) => p.sku === sku);
    if (found) {
      state.doorSize = {
        h: found.h,
        w: found.w,
        sku: found.sku,
        price: Number(found.price) || 0,
        label: found.label ?? "",
        type: found.type,
        category: found.category ?? "",
        ...(found.discountCode ? { discountCode: found.discountCode } : {}),
      };
    }
    paint();
  }

  function addDoorToQuote(): void {
    const size = state.doorSize;
    const range = state.doorRange;
    if (!size || !range) return;
    // Session-level bespoke ref applies to every bespoke add until the
    // retailer changes it (Steve, 2026-05-26). Sync into the per-line field
    // before the rest of the add path runs so the existing colourLabel /
    // notes / QuoteItem code keeps working unchanged.
    if (state.doorType === "bespoke") {
      state.doorBespokeText = sessionBespokeRef.trim();
    }
    const colourLabel = state.doorType === "bespoke"
      ? (state.doorBespokeText || "Bespoke")
      : state.doorType === "primed"
        ? "Primed"
        : (state.doorColour || "");
    const typeLabel =
      state.doorType === "signature" ? " [Sig Select]" :
      state.doorType === "pto"       ? " [PTO]" :
      state.doorType === "bespoke"   ? " [Bespoke]" :
      state.doorType === "primed"    ? " [Primed]" : "";
    // Variant suffix from catalogue (Curved / Glazed / Frame). Drives the
    // dimensions string too — curved doors have no width so they read as
    // "<height>mm Curved" rather than "<h>×<w>mm".
    const variantSuffix = typeSuffix(size.type);
    const baseDims = size.type === "curved"
      ? `${size.h}mm Curved`
      : `${size.h}×${size.w}mm${variantSuffix ? ` ${variantSuffix}` : ""}`;
    // `size.label` is the handing / grain / variant descriptor (Left, Right,
    // Vertical Grain …) recovered from the catalogue name. Append it so the
    // saved line keeps both the dimensions and the descriptive text.
    const dimsLabel = size.label ? `${baseDims} ${size.label}` : baseDims;
    const desc = `${range.name} ${colourLabel}${typeLabel} ${dimsLabel}`.trim();

    const supplierLabel = supCtx.label;
    // PG-configured discount only (no supplier default). Looked up against
    // the size's PG catalogue category + the picked range.
    const discPct = resolveDiscPct(supCtx.slug, size.category || null, range.name, {
      discountCode: size.discountCode ?? null, doorColour: state.doorColour || null, code: size.sku,
    }) ?? 0;

    const handingLabel: "left" | "right" | "top" =
      state.doorSide === "T" ? "top" : state.doorSide === "R" ? "right" : "left";

    const drillingPositions = state.doorDrillMode === "hinge"
      ? [state.doorTopMm, ...(state.doorHoleMidMm ?? []), size.h - state.doorBottomMm]
      : [];

    const noteParts: string[] = [];
    if (state.doorType === "bespoke") noteParts.push(`BESPOKE: ${state.doorBespokeText || ""}`);
    else if (state.doorType === "pto") noteParts.push("PTO");
    else if (state.doorType === "signature") noteParts.push("SIGNATURE SELECT");
    else if (state.doorType === "primed") noteParts.push("PRIMED — ready to paint on site");
    if (state.doorDrillMode === "hinge") {
      const qty = state.doorHoleQty || 2;
      const mids = state.doorHoleMidMm ?? [];
      const parts = [`top ${state.doorTopMm}mm`];
      for (let i = 0; i < qty - 2; i++) parts.push(`hole ${i + 2} ${mids[i] ?? "?"}mm`);
      parts.push(`bottom ${state.doorBottomMm}mm`);
      noteParts.push(`${qty}-hole boring: ${parts.join(" / ")}`);
      // Handing + unit type only matter when drilling is specified (Steve,
      // 2026-05-26). Unit type is needed for the drilling spec — base vs
      // wall affects hinge positioning. Hidden when drillMode === "none".
      noteParts.push(`Handing: ${state.doorSide === "L" ? "Left" : state.doorSide === "R" ? "Right" : "Top"}`);
      noteParts.push(`${state.doorUnitType.charAt(0).toUpperCase()}${state.doorUnitType.slice(1)} unit`);
    }

    const item: QuoteItem = {
      id: newId("i"),
      category: "doors",
      supplier: supplierLabel,
      code: size.sku || `TKC-DOOR-${size.h}x${size.w}`,
      desc,
      qty: state.doorQty || 1,
      width: size.w,
      height: size.h,
      depth: 0,
      notes: noteParts.filter(Boolean).join(" | "),
      listPrice: Number(size.price) || 0,
      discPct,
      tradePrice: 0,
      markupMode: "pct",
      markupPct: store.get().defaults.markupPct ?? 0,
      markupGbp: 0,
      retailPrice: 0,
      doorRange: range.name,
      doorColour: state.doorColour,
      handing: handingLabel,
      drillingPositions,
      drillingFrom: state.doorDrillMode === "hinge" ? "top" : "",
      topMm: state.doorTopMm,
      bottomMm: state.doorBottomMm,
      doorType: state.doorType,
      doorBespokeText: state.doorBespokeText,
      doorUnitType: state.doorUnitType,
      doorHoleQty: state.doorDrillMode === "hinge" ? state.doorHoleQty : 0,
      doorHoleMidMm: state.doorHoleMidMm,
    };
    recalcItem(item, null);

    const zoneId = state.zoneId || store.get().zones[0]?.id || "";
    store.set((d) => {
      const z = d.zones.find((zo) => zo.id === zoneId) ?? d.zones[0];
      if (z) z.items.push(item);
    });

    // ── Drilling charge line item ────────────────────────────────────────
    // When the door is configured with hinge drilling AND the supplier has
    // set a per-hole drilling charge, append a separate Drilling line so
    // the customer sees it as its own cost on the quote.
    if (state.doorDrillMode === "hinge") {
      const perHole = resolveDrillingCharge(supCtx.slug);
      const holeQty = state.doorHoleQty || 2;
      if (perHole && perHole > 0 && holeQty > 0) {
        const perDoor = Math.round(perHole * holeQty * 100) / 100;
        const drilling: QuoteItem = {
          id: newId("i"),
          category: "sundries",
          supplier: supplierLabel,
          code: `${range.name.replace(/\s+/g, "-").toUpperCase()}-DRILLING`,
          desc: `Drilling charge (${holeQty} hole${holeQty === 1 ? "" : "s"})`,
          qty: state.doorQty || 1,
          width: 0,
          height: 0,
          depth: 0,
          notes: `${holeQty} × £${perHole.toFixed(2)} per hole · for ${desc}`,
          listPrice: perDoor,
          discPct: 0,
          tradePrice: 0,
          markupMode: "pct",
          markupPct: store.get().defaults.markupPct ?? 0,
          markupGbp: 0,
          retailPrice: 0,
        };
        recalcItem(drilling, null);
        store.set((d) => {
          const z = d.zones.find((zo) => zo.id === zoneId) ?? d.zones[0];
          if (z) z.items.push(drilling);
          // Collapse it into the quote's existing Hinge drilling row rather
          // than adding the 21st of them. Value-preserving or it refuses —
          // see drilling-aggregate.ts. Same store.set, so this is one
          // mutation and one autosave, not two.
          aggregateDrilling(d);
        });
      }
    }

    // Reset size only — keep range+colour for fast repeat adds (source 9790).
    state.doorSize = null;
    paint();
  }

  function addBespokeManual(): void {
    const range = state.doorRange;
    if (!range) return;
    // Same session-level ref sync as addDoorToQuote so the no-size fallback
    // also uses the top-of-configurator reference when one is set.
    state.doorBespokeText = sessionBespokeRef.trim() || state.doorBespokeText;
    const colourLabel = state.doorBespokeText || "Bespoke";
    const desc = `${range.name} ${colourLabel} [Bespoke] — size TBC`;
    const supplierLabel = supCtx.label;
    // Bespoke has no PG catalogue category — match by range only against
    // the PG discount cache; fall through to 0 if nothing configured.
    const discPct = resolveDiscPct(supCtx.slug, null, range.name, {
      doorColour: state.doorColour || null,
    }) ?? 0;
    const item: QuoteItem = {
      id: newId("i"),
      category: "doors",
      supplier: supplierLabel,
      code: `BESPOKE-${range.name.replace(/\s+/g, "-").toUpperCase()}`,
      desc,
      qty: state.doorQty || 1,
      width: 0,
      height: 0,
      depth: 0,
      notes: `BESPOKE: ${colourLabel} — price TBC`,
      listPrice: 0,
      discPct,
      tradePrice: 0,
      markupMode: "pct",
      markupPct: store.get().defaults.markupPct ?? 0,
      markupGbp: 0,
      retailPrice: 0,
      doorRange: range.name,
      doorColour: colourLabel,
      handing: state.doorSide === "T" ? "top" : state.doorSide === "R" ? "right" : "left",
      drillingPositions: [],
      drillingFrom: "",
      doorType: "bespoke",
      doorBespokeText: state.doorBespokeText,
      doorUnitType: state.doorUnitType,
    };
    recalcItem(item, null);
    const zoneId = state.zoneId || store.get().zones[0]?.id || "";
    store.set((d) => {
      const z = d.zones.find((zo) => zo.id === zoneId) ?? d.zones[0];
      if (z) z.items.push(item);
    });
    state.doorSize = null;
    state.doorBespokeText = "";
    paint();
  }

  paint();

  return {
    unmount(): void {
      clearBreadcrumb();
      host.innerHTML = "";
    },
  };
}

// ── Render ───────────────────────────────────────────────────────────────

function render(s: DoorPickerState, defaults: DoorDefaults, store: QuoteStore): string {
  const q = store.get();
  void defaults;

  // Zone options
  const zoneOpts = q.zones.map(
    (z) => `<option value="${escapeHtml(z.id)}"${z.id === s.zoneId ? " selected" : ""}>${escapeHtml(z.name)}</option>`,
  ).join("");

  // Range fuzzy combo
  const rangeCombo = `
    <div class="qb-door-combo-wrap">
      <input type="text" class="qb-door-input" data-role="range-input" value="${escapeHtml(s.rangeInput)}" placeholder="Pick a range — e.g. Cartmel, Cambridge, Bella…" autocomplete="off" />
      <div class="qb-door-combo" data-role="range-combo" role="listbox"></div>
    </div>
  `;

  // ── Unified colour <select> — catalogue-driven, scoped to the active
  // tier only (2026-05-26 strict-isolation fix). Each per-tier range card
  // owns its own colour surface:
  //   • Stock / Signature  → this unified dropdown, with ONLY that tier's
  //                          colours rendered. Cross-tier colours must not
  //                          leak in — Steve, "stock type boundaries are
  //                          respected strictly".
  //   • PTO     → dedicated `ptoColourBlock` head dropdown (palette-driven).
  //   • Bespoke → free-text colour reference at top of configurator.
  //   • Primed  → no colour at all (static "ready to paint" indicator).
  // Pre-2026-05-26 endpoints don't return `coloursByType`; we fall back to
  // treating the flat `colours` list as stock so existing uploads keep
  // rendering something for the Stock card.
  // Bespoke has no dropdown (free-text reference) and Primed has no colour
  // at all, so `cbt.bespoke` / `cbt.primed` aren't unpacked here. Stock /
  // Signature feed the unified dropdown below; PTO feeds the dedicated
  // head-block dropdown built further down.
  const cbt = s.doorRange?.coloursByType;
  const stockColours     = cbt ? cbt.stock     : (s.doorRange?.colours ?? []);
  const ptoColours       = cbt ? cbt.pto       : [];
  const signatureColours = cbt ? cbt.signature : [];

  // The unified dropdown only ever paints for stock / signature flows, so
  // build its options from exactly the active tier's bucket. Any cross-tier
  // colour bleed-in is a regression.
  let unifiedTierColours: readonly string[] = [];
  let unifiedTierLabel = "";
  let unifiedTier: DoorType = "stock";
  if (s.doorType === "signature") {
    unifiedTierColours = signatureColours;
    unifiedTierLabel = "Signature · stock lead time";
    unifiedTier = "signature";
  } else {
    unifiedTierColours = stockColours;
    unifiedTierLabel = "Stocked colours · fast delivery";
    unifiedTier = "stock";
  }

  // One tier at a time here, by design — this flow picks a tier first and
  // cross-tier bleed-in is a regression. The SJB picker passes every non-empty
  // tier to the same renderer; the module does not choose.
  const colourBlock = renderColourSelect({
    groups: [{ tier: unifiedTier, colours: unifiedTierColours, label: unifiedTierLabel }],
    selectedTier: s.doorType,
    selectedColour: s.doorColour,
    role: "colour-select",
    emptyText: `No ${unifiedTier === "signature" ? "signature" : "stocked"} colours uploaded for this range yet`,
  });
  // Lead-time / pricing badge — variant follows the picked tier. `doorType`
  // is set by setColourUnified() the moment a colour is selected, so it's
  // the source of truth here.
  const tierBadge = renderTierBadge(s.doorColour, s.doorType);

  // ── Size dropdowns: Height → Width + resolved-size strip ─────────────
  //
  // Curved doors (added 2026-05-25) carry w=0 (server parses height from
  // name); they share the same height bucket as standard rows and appear
  // as a "Curved" entry in the width dropdown. Glazed/Frame rows live at
  // the same (h, w) as their standard counterpart — the dropdown lists
  // each as a separate option labelled "(Glazed)" / "(Frame)" and the
  // select uses SKU as value since (h, w) is no longer unique.
  const validSizes = (s.sizes ?? []).filter((p) =>
    p.h > 0 &&
    (p.w > 0 || p.type === "curved") &&
    !PANEL_EXCL.some(([ph, pw]) => ph === p.h && pw === p.w),
  );
  const heights: number[] = [];
  const heightSeen = new Set<number>();
  for (const p of validSizes) {
    if (!heightSeen.has(p.h)) { heightSeen.add(p.h); heights.push(p.h); }
  }
  heights.sort((a, b) => a - b);
  const selectedH = s.doorSize?.h ?? null;
  const selectedSku = s.doorSize?.sku ?? "";

  // Build width options for the selected height: one per row, since
  // Standard / Glazed / Frame can coexist at the same width.
  // Sort: standard first (by width asc), then glazed (by width asc), then
  // frame (by width asc), then curved at the end.
  const TYPE_ORDER: Record<SizeType, number> = { standard: 0, glazed: 1, frame: 2, curved: 3 };
  const widthOptions = selectedH === null
    ? []
    : validSizes
        .filter((p) => p.h === selectedH)
        .slice()
        .sort((a, b) => {
          const t = TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
          if (t !== 0) return t;
          return a.w - b.w;
        });
  const resolved = selectedSku
    ? validSizes.find((p) => p.sku === selectedSku)
    : undefined;

  function widthLabel(p: CatalogueSize): string {
    const desc = p.label ? ` ${p.label}` : "";
    if (p.type === "curved") return "Curved" + desc;
    const suf = typeSuffix(p.type);
    return p.w + "mm" + (suf ? ` (${suf})` : "") + desc;
  }

  // Flow ordering:
  //   Stock / Signature / PTO — colour first, then sizes (Steve, 2026-05-26:
  //     "Colour must be visible and selectable before any size is entered").
  //     PTO uses a dedicated palette dropdown in the head block instead of
  //     the unified Stock/Sig/PTO/Bespoke optgroup select.
  //   Bespoke — colour reference is free-text at top of configurator; sizes
  //     render straight away (no dropdown gate).
  //   Primed — no colour at all; sizes render straight away.
  const isPtoFlow     = s.doorType === "pto";
  const isBespokeFlow = s.doorType === "bespoke";
  const isPrimedFlow  = s.doorType === "primed";
  const showSizes = !!s.doorRange && (
    isBespokeFlow
    || isPrimedFlow
    || !!s.doorColour
  );

  let sizeBlock = "";
  if (showSizes && s.loadingSizes) {
    sizeBlock = `<div class="qb-door-loading">Loading sizes…</div>`;
  } else if (showSizes) {
    if (validSizes.length === 0) {
      sizeBlock = `<div class="qb-door-empty">No sizes found for this ${isBespokeFlow || isPrimedFlow ? "range" : "colour"}</div>`;
    } else {
      const hOpts = heights.map((h) => `<option value="${h}"${selectedH === h ? " selected" : ""}>${h}mm</option>`).join("");
      const wOpts = widthOptions.map((p) =>
        `<option value="${escapeHtml(p.sku)}"${selectedSku === p.sku ? " selected" : ""}>${escapeHtml(widthLabel(p))}</option>`,
      ).join("");
      const resolvedSuffix = resolved ? typeSuffix(resolved.type) : "";
      const resolvedDims = resolved
        ? (resolved.type === "curved"
            ? `${resolved.h}mm Curved`
            : `${resolved.h} × ${resolved.w}mm${resolvedSuffix ? ` (${resolvedSuffix})` : ""}`)
        : "";
      sizeBlock = `
        <div class="qb-door-row two">
          <div>
            <label class="qb-door-label">Height (mm)</label>
            <select class="qb-door-input" data-role="height-select">
              <option value="">— select —</option>${hOpts}
            </select>
          </div>
          <div>
            <label class="qb-door-label">Width${selectedH ? "" : ` <em class="qb-door-label-hint">(select height first)</em>`}</label>
            <select class="qb-door-input" data-role="width-select" ${selectedH ? "" : "disabled"}>
              <option value="">— select —</option>${wOpts}
            </select>
          </div>
        </div>
        ${resolved ? `
          <div class="qb-door-resolved">
            <span class="qb-door-resolved__dims">${escapeHtml(resolvedDims)}${resolved.label ? ` — ${escapeHtml(resolved.label)}` : ""}</span>
            <span class="qb-door-resolved__price">${fmtGbp(resolved.price)}</span>
            <span class="qb-door-resolved__suffix">list</span>
          </div>
        ` : ""}
        <div class="qb-door-hint">${heights.length} height${heights.length === 1 ? "" : "s"} · ${validSizes.length} sizes total</div>
      `;
    }
  }

  const sizeLockedIn = !!(s.doorSize && s.doorSize.sku && (s.doorSize.w > 0 || s.doorSize.type === "curved"));

  // ── PTO colour picker (head block, between Style/Range and Height) ───
  // The PTO palette comes from coloursByType.pto (supplier-level palette
  // merged with any per-range PTO product colours by /door-ranges). Empty
  // palette short-circuits to a hint — Add stays disabled until a colour
  // is picked because tierReady checks `!!s.doorColour`.
  let ptoColourBlock = "";
  if (isPtoFlow) {
    const palette = ptoColours;
    if (palette.length === 0) {
      ptoColourBlock = `
        <div class="qb-door-row">
          <div>
            <label class="qb-door-label">Paint to Order colour</label>
            <div class="qb-door-empty">No PTO colours set up for this range yet.</div>
          </div>
        </div>`;
    } else {
      const opts = palette.map((c) =>
        `<option value="${escapeHtml(c)}"${s.doorColour === c ? " selected" : ""}>${escapeHtml(c)}</option>`,
      ).join("");
      ptoColourBlock = `
        <div class="qb-door-row">
          <div>
            <div class="qb-door-label-row">
              <label class="qb-door-label">Paint to Order colour<span class="qb-door-req"> *</span></label>
              <span class="qb-door-label-hint">${palette.length} colour${palette.length === 1 ? "" : "s"}</span>
            </div>
            <select class="qb-door-input" data-role="pto-colour-select">
              <option value="">— Select a PTO colour —</option>${opts}
            </select>
            ${s.doorColour ? `<div class="qb-door-tier qb-door-tier--pto">✓ ${escapeHtml(s.doorColour)} · Paint to order · longer lead time</div>` : ""}
          </div>
        </div>`;
    }
  }
  // Bespoke flow renders the colour-reference input at the TOP of the
  // configurator (see the head block in the final return below). One
  // session-level value applies to every bespoke door added until the
  // retailer edits it — see `sessionBespokeRef`.

  // ── Drilling & Handing card ─────────────────────────────────────────
  // Rendered by the shared block so the SJB cabinet picker embeds the same
  // component rather than a copy. Extracted 2026-09-04; the baseline harness
  // is what proves the move changed nothing here.
  const drillingCard = renderDrillingBlock({
    mode: s.doorDrillMode,
    unitType: s.doorUnitType,
    side: s.doorSide,
    holeQty: s.doorHoleQty,
    topMm: s.doorTopMm,
    bottomMm: s.doorBottomMm,
    midMm: s.doorHoleMidMm ?? [],
  }, { id: "door" });

  // ── Panels & Accessories section (renders when a range is bound) ────
  // Rendered by the shared module so the SJB frontal flow reaches the same
  // panels without a second trip through this picker. Extracted 2026-09-04.
  const extrasBlock = s.doorRange
    ? renderExtrasSection({
        extras: s.extras ?? [],
        open: s.extrasOpen,
        loading: s.loadingExtras,
        qtyById: s.extrasQty,
      })
    : "";

  // ── Footer ──────────────────────────────────────────────────────────
  // Add is enabled once a size is locked in PLUS any tier-specific
  // requirements:
  //   • PTO     → requires a PTO colour to be picked.
  //   • Bespoke → requires a colour reference to be entered.
  //   • Stock / Signature → size alone is enough.
  // Curved doors carry w=0 by design (height is the only dimension); allow
  // them via the type check.
  const tierReady =
    isPtoFlow     ? !!s.doorColour :
    isBespokeFlow ? !!sessionBespokeRef.trim() :
    true;
  const canAdd = sizeLockedIn && tierReady;
  // Manual-bespoke escape hatch ("Add without size") only makes sense in
  // the legacy bespoke flow when no size is bound. The post-size bespoke
  // flow above already covers normal use.
  const manualBespoke = isBespokeFlow && !s.doorSize && s.doorBespokeText;
  const footer = `
    <div class="qb-door-foot">
      <div class="qb-door-foot__hint"><kbd>Enter</kbd> add</div>
      <div class="qb-door-foot__actions">
        <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Cancel</button>
        <button type="button" class="qb-btn qb-btn--primary" data-role="add-door" ${canAdd ? "" : "disabled"}>${svgIcon(ICONS.plus)} Add door</button>
        ${manualBespoke ? `<button type="button" class="qb-btn qb-btn--ghost qb-door-foot__manual" data-role="add-door-manual">Add without size</button>` : ""}
      </div>
    </div>
  `;

  return `
    <div class="qb-door-picker__inner">
      <div class="qb-door-picker__head">
        <span class="qb-door-picker__title">${s.doorRange ? `<span class="qb-door-sup-chip">TKC</span> ${escapeHtml(s.doorRange.name)} — Door Configurator` : "Door Configurator"}</span>
        <span class="qb-door-picker__hint">${q.zones.length} zone${q.zones.length === 1 ? "" : "s"}</span>
      </div>

      <div class="qb-door-row">
        <div>
          <div class="qb-door-label-row">
            <label class="qb-door-label">Style / range</label>
            <label class="qb-door-remember"><input type="checkbox" data-role="remember-range" ${q.defaults.remember.range ? "checked" : ""}/> Remember</label>
          </div>
          ${rangeCombo}
        </div>
      </div>

      ${s.doorRange ? `
        ${isBespokeFlow ? `
          <div class="qb-door-row qb-door-bespoke-ref">
            <div>
              <div class="qb-door-label-row">
                <label class="qb-door-label" for="qb-bespoke-ref-input">Colour reference (RAL, Farrow &amp; Ball, or custom)<span class="qb-door-req"> *</span></label>
                <span class="qb-door-label-hint">applies to every bespoke door this session</span>
              </div>
              <input id="qb-bespoke-ref-input" class="qb-door-input" type="text" maxlength="200"
                     placeholder='e.g. &quot;RAL 9010&quot; or &quot;F&amp;B Elephant&apos;s Breath&quot;'
                     value="${escapeHtml(sessionBespokeRef)}" data-role="session-bespoke-ref" />
              ${sessionBespokeRef ? `<div class="qb-door-tier qb-door-tier--bespoke">✓ ${escapeHtml(sessionBespokeRef)} · Bespoke · made to order</div>` : ""}
            </div>
          </div>
        ` : isPtoFlow ? `
          ${ptoColourBlock}
        ` : isPrimedFlow ? `
          <div class="qb-door-row">
            <div>
              <div class="qb-door-tier qb-door-tier--primed">✓ Primed · ready to paint on site</div>
            </div>
          </div>
        ` : `
          <div class="qb-door-row">
            <div>
              <div class="qb-door-label-row">
                <label class="qb-door-label">Colour</label>
                <label class="qb-door-remember"><input type="checkbox" data-role="remember-finish" ${q.defaults.remember.finish ? "checked" : ""}/> Remember</label>
              </div>
              ${colourBlock}
              ${tierBadge}
            </div>
          </div>
        `}
        ${sizeBlock}

        <div class="qb-door-row two">
          <div>
            <label class="qb-door-label">Zone</label>
            <select class="qb-door-input" data-role="zone">${zoneOpts}</select>
          </div>
          <div>
            <label class="qb-door-label">Qty</label>
            <input class="qb-door-input" type="number" min="1" value="${s.doorQty}" data-role="qty" />
          </div>
        </div>

        ${drillingCard}
        ${extrasBlock}
      ` : `<div class="qb-door-hint qb-door-pick-range">Pick a range above to load colours and sizes.</div>`}

      ${footer}
    </div>
  `;
}
